import * as RadixDialog from "@radix-ui/react-dialog";
import classNames from "classnames/bind";
import React, { useCallback, useRef, useState } from "react";
import { Button } from "../Button";
import styles from "./ImageLightbox.module.scss";

const cx = classNames.bind(styles);

type PositionType = { x: number; y: number };

export type ImageLightboxProps = {
  /** Whether the lightbox is open */
  isOpen: boolean;
  /** Callback fired when the lightbox should close */
  onClose: () => void;
  /** The image source URL */
  imageSrc: string;
  /** Alt text for the image */
  imageAlt?: string;
};

/**
 * A fullscreen lightbox component for viewing images with zoom capability.
 * Uses Radix Dialog for accessibility and focus management.
 * Supports drag/pan when zoomed in.
 */
/** Minimum distance in pixels to consider a movement as a drag vs a click */
const DRAG_THRESHOLD = 5;

const ImageLightbox = ({
  isOpen,
  onClose,
  imageSrc,
  imageAlt = "",
}: ImageLightboxProps) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState<PositionType>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<PositionType>({ x: 0, y: 0 });
  const positionStartRef = useRef<PositionType>({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);

  const resetState = useCallback(() => {
    setIsZoomed(false);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    hasDraggedRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleImageClick = useCallback(() => {
    // Only toggle zoom if user didn't drag (to avoid accidental zoom on drag end)
    if (!hasDraggedRef.current) {
      setIsZoomed((prev) => {
        if (prev) {
          // Reset position when zooming out
          setPosition({ x: 0, y: 0 });
        }
        return !prev;
      });
    }
    hasDraggedRef.current = false;
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!isZoomed) return;
      event.preventDefault();
      setIsDragging(true);
      hasDraggedRef.current = false;
      dragStartRef.current = { x: event.clientX, y: event.clientY };
      positionStartRef.current = { ...position };
    },
    [isZoomed, position]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging || !isZoomed) return;
      const deltaX = event.clientX - dragStartRef.current.x;
      const deltaY = event.clientY - dragStartRef.current.y;

      // Only consider it a drag if moved beyond threshold
      if (
        Math.abs(deltaX) > DRAG_THRESHOLD ||
        Math.abs(deltaY) > DRAG_THRESHOLD
      ) {
        hasDraggedRef.current = true;
      }

      setPosition({
        x: positionStartRef.current.x + deltaX,
        y: positionStartRef.current.y + deltaY,
      });
    },
    [isDragging, isZoomed]
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const imageTransform = isZoomed
    ? `scale(2) translate(${position.x / 2}px, ${position.y / 2}px)`
    : undefined;

  return (
    <RadixDialog.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={cx("overlay")} />
        <RadixDialog.Content
          className={cx("content")}
          aria-label="Image preview"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <Button
            variant="outline"
            icon="x"
            className={cx("close-button")}
            onClick={handleClose}
            aria-label="Close image preview"
          />
          <div className={cx("image-container")}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className={cx("image", {
                "is-zoomed": isZoomed,
                "is-dragging": isDragging,
              })}
              onClick={handleImageClick}
              onPointerDown={handlePointerDown}
              style={{ transform: imageTransform }}
              draggable={false}
            />
          </div>
          <p className={cx("zoom-hint")}>
            {isZoomed
              ? "Drag to pan, click to zoom out"
              : "Click image to zoom in"}
          </p>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default ImageLightbox;
